import { Collapse } from '@components/animators/collapse';
import { EmptyStateCell } from '@components/empty-state';
import { IconContainerVariant } from '@components/icon-fancy-container/icon-fancy-container';
import { IconApi } from '@components/icons/api';
import { IconIntegration } from '@components/icons/integration';
import { IconWebhook } from '@components/icons/webhook';
import { IntegrationIcon, methodFancyContainerColorMap } from '@components/integrations/common';
import { Integration } from '@components/integrations/integrations-types';
import { Spinner } from '@components/loading-spinner/loading-spinner';
import { useScopedTranslate } from '@locale/translate';
import { createSelectionStore } from '@stores/create-selection-store';

import {
  ActionItem,
  ActionsList,
  GroupHeader,
  ItemIcon,
  OnCreateActionSelectProps,
  SearchEmptyState,
  TabContentWrapper,
} from './common';

const collapsedIntegrationsStore = createSelectionStore({
  persistKey: 'add-action-modal-collapsed-integrations',
});

type CustomIntegrationsTabProps = {
  integrations: Integration[] | undefined;
  isLoading: boolean;
  onSelect: (props: OnCreateActionSelectProps) => void;
  searchQuery?: string;
  onClearSearch: () => void;
};

export const CustomIntegrationsTab = ({
  integrations,
  isLoading,
  onSelect,
  searchQuery,
  onClearSearch,
}: CustomIntegrationsTabProps) => {
  const { t } = useScopedTranslate(locale);

  const hasNoSearchResults = searchQuery && integrations?.length === 0;

  return (
    <TabContentWrapper>
      {isLoading ? (
        <Spinner />
      ) : hasNoSearchResults ? (
        <SearchEmptyState onClearSearch={onClearSearch} />
      ) : integrations && integrations.length > 0 ? (
        <div>
          {integrations.map((integration) => (
            <IntegrationGroup
              key={integration.shortId}
              integration={integration}
              onSelect={onSelect}
            />
          ))}
        </div>
      ) : (
        <EmptyStateCell icon={<IconIntegration />} text={t('noCustomIntegrations')} />
      )}
    </TabContentWrapper>
  );
};

type IntegrationGroupProps = {
  integration: Integration;
  onSelect: (props: OnCreateActionSelectProps) => void;
};

const IntegrationGroup = ({ integration, onSelect }: IntegrationGroupProps) => {
  const isCollapsed = collapsedIntegrationsStore.useStore((state) => state[integration.shortId]);

  return (
    <div>
      <GroupHeader
        onClick={() => collapsedIntegrationsStore.toggle(integration.shortId)}
        isCollapsed={isCollapsed}
        text={integration.name}
        textFont="semibold14"
        icon={<IntegrationIcon src={integration.thumbnail ?? integration.logo} size="sm" />}
      />
      <Collapse isCollapsed={isCollapsed}>
        <ActionsList>
          {integration.endpoints.map((item) => (
            <IntegrationActionItem
              key={item.shortId}
              item={item}
              variant="endpoint"
              onSelect={onSelect}
            />
          ))}
          {integration.webhooks.map((item) => (
            <IntegrationActionItem
              key={item.shortId}
              item={item}
              variant="webhook"
              onSelect={onSelect}
            />
          ))}
        </ActionsList>
      </Collapse>
    </div>
  );
};

type IntegrationActionItemProps = {
  item: {
    shortId: string;
    name: string;
    description?: string;
    method: keyof typeof methodFancyContainerColorMap;
  };
  variant: 'endpoint' | 'webhook';
  onSelect: (props: OnCreateActionSelectProps) => void;
};

const integrationActionConfig = {
  endpoint: {
    icon: IconApi,
    type: 'integration' as const,
    actionType: 'integration' as const,
    valueKey: 'integrationEndpointId',
  },
  webhook: {
    icon: IconWebhook,
    type: 'webhook' as const,
    actionType: 'webhook' as const,
    valueKey: 'integrationWebhookId',
  },
};

const IntegrationActionItem = ({ item, variant, onSelect }: IntegrationActionItemProps) => {
  const config = integrationActionConfig[variant];

  return (
    <ActionItem
      text={item.name}
      description={item.description ?? ''}
      icon={
        <ItemIcon
          icon={config.icon}
          variant={methodFancyContainerColorMap[item.method] as IconContainerVariant}
        />
      }
      type={config.type}
      onSelect={() =>
        onSelect({
          actionType: config.actionType,
          values: { [config.valueKey]: { value: item.shortId } },
        })
      }
    />
  );
};

const locale = {
  en: {
    noCustomIntegrations: 'No custom integrations configured',
  },
};
